import React, {useCallback, useRef, useState} from "react";
import Cropper, {Area} from "react-easy-crop";
import {useDropzone} from "react-dropzone";
import {url} from "../../utils/UrlsUtil.ts";
import {Image} from "react-bootstrap";

const TARGET_WIDTH_PX = 1920;
const TARGET_HEIGHT_PX = 1280;
const ASPECT_RATIO = TARGET_WIDTH_PX / TARGET_HEIGHT_PX; //rate: 1.5:1
interface MyDesignImageCropperProps {
    dto?: DesignTemplateDetailDto;
    add_class_name?: string;
    handleCrop?: (file: File | null) => void;
}
interface CroppedAreaPixels extends Area {}
const MyDesignImageCropper: React.FC<MyDesignImageCropperProps> = (
    {
        dto = undefined,
        add_class_name = "",
        handleCrop = () => {},
    }) =>
{
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [localError, setLocalError] = useState<string>('');
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const resetCropper = useCallback(() => {
        setImageSrc(null);
        setLocalError('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        if (dto) dto.file = null; // clear file
        if (handleCrop) handleCrop(null);
    }, [dto, handleCrop]);
    const generateCroppedFile = useCallback(async (pixels: CroppedAreaPixels) => {
        if (!imageSrc || !dto) return;

        try {
            const image = new window.Image();
            image.src = imageSrc;
            await new Promise((resolve, reject) => {
                image.onload = resolve;
                image.onerror = reject;
            });

            const canvas = document.createElement('canvas');
            canvas.width = TARGET_WIDTH_PX;
            canvas.height = TARGET_HEIGHT_PX;
            const ctx = canvas.getContext('2d');

            if (!ctx) return;

            ctx.drawImage(
                image,
                pixels.x,
                pixels.y,
                pixels.width,
                pixels.height,
                0,
                0,
                TARGET_WIDTH_PX,
                TARGET_HEIGHT_PX
            );

            canvas.toBlob((blob) => {
                if (blob) {
                    // save cropped file to dto
                    dto.file = new File([blob], "card_design.jpg", {type: 'image/jpeg'});
                    if (handleCrop) {
                        handleCrop(dto.file);
                    }
                }
            }, 'image/jpeg', 0.9);
        } catch (e) {
            console.error("Error cropping image:", e);
        }
    }, [imageSrc, dto, handleCrop]);
    const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: CroppedAreaPixels) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            generateCroppedFile(croppedAreaPixels);
        }, 300);

    }, [generateCroppedFile]);
    const onDrop = useCallback((acceptedFile: File[]) => {
        if (acceptedFile.length > 0) {
            const file = acceptedFile[0];
            const reader = new FileReader();
            reader.onload = () => {
                const img = new window.Image();
                img.src = reader.result as string;
                img.onload = () => {
                    if (img.width < TARGET_WIDTH_PX || img.height < TARGET_HEIGHT_PX) {
                        setLocalError(`画像サイズが足りません。${TARGET_WIDTH_PX}px×${TARGET_HEIGHT_PX}px 以上の画像を選択してください。`);
                        return;
                    }
                    setImageSrc(reader.result as string);
                    setLocalError('');
                };
            };
            reader.readAsDataURL(file);
        }
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] },
    });
    const adjustZoom = (delta: number) => {
        setZoom((prev) => Math.min(Math.max(prev + delta, 1), 3));
    };
    return (
        <div className={`my-design-image-cropper ${add_class_name}`}>
            <label className="my-design-input-title">{dto?.title}</label>
            <div className="image-cropper">
                {!imageSrc ? (
                    <div {...getRootProps()} className="dropzone-area">
                        <input {...getInputProps()} />
                        <div className="text-center p-4" style={{width: '100%', border: '2px dashed #ccc', cursor: 'pointer' }}>
                            {isDragActive ? (
                                <p className="small text-muted">こちらにドラッグ＆ドロップしてください...</p>
                            ) : (
                                <>
                                    <p className="small text-muted">写真をドラッグ＆ドロップ</p>
                                    <Image className="image-icon" src={url('/images/icon/image_upload_icon.png')} fluid={true} draggable={false} />
                                    <p className="small text-muted">{TARGET_WIDTH_PX}px × {TARGET_HEIGHT_PX}px 以上</p>
                                </>
                            )}
                        </div>
                        {localError && <p className="small text-danger mt-2" style={{lineHeight: '1.5'}}>{localError}</p>}
                    </div>
                ) : (
                    <div className="cropper-wrapper">
                        <div className="cropper-container" style={{ position: 'relative', height: '300px', width: '100%' }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={ASPECT_RATIO}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="controls mt-3 text-center">
                            {/* Zoom percent */}
                            <div className="zoom-percentage mb-2">
                                <strong>{Math.round(zoom * 100)}%</strong>
                            </div>

                            <div className="d-flex align-items-center justify-content-center gap-2">
                                {/* Zoom -- */}
                                <button
                                    type="button"
                                    className="btn btn-dark"
                                    style={{borderRadius: '50%', fontWeight: '600'}}
                                    onClick={() => adjustZoom(-0.1)}
                                    disabled={zoom <= 1}
                                >
                                    －
                                </button>

                                <input
                                    type="range"
                                    style={{ width: '80%' }}
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                />

                                {/* Zoom ++ */}
                                <button
                                    type="button"
                                    className="btn btn-dark"
                                    style={{borderRadius: '50%', fontWeight: '600'}}
                                    onClick={() => adjustZoom(0.1)}
                                    disabled={zoom >= 3}
                                >
                                    ＋
                                </button>
                            </div>

                            {/* Reset */}
                            <button className="btn btn-danger mt-3" onClick={resetCropper}>
                                リセット
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default MyDesignImageCropper;